import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Shield, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Parse partner query params (Mode A)
  const partnerParams = useMemo(() => {
    return {
      partner: searchParams.get("partner") || "educrib",
      educrib_session_id: searchParams.get("educrib_session_id"),
      educrib_user_id: searchParams.get("educrib_user_id"),
      prefill_path_type: searchParams.get("prefill_path_type"),
      prefill_location: searchParams.get("prefill_location"),
      prefill_program_interest: searchParams.get("prefill_program_interest"),
      sessionId: searchParams.get("sessionId"),
    };
  }, [searchParams]);

  // Create session on load
  useEffect(() => {
    if (partnerParams.sessionId) {
      setSessionId(partnerParams.sessionId);
      sessionStorage.setItem("synaptick_session_id", partnerParams.sessionId);
      sessionStorage.setItem("synaptick_partner", partnerParams.partner);
      if (partnerParams.prefill_path_type) sessionStorage.setItem("prefill_path_type", partnerParams.prefill_path_type);
      if (partnerParams.prefill_location) sessionStorage.setItem("prefill_location", partnerParams.prefill_location);
      if (partnerParams.prefill_program_interest) sessionStorage.setItem("prefill_program_interest", partnerParams.prefill_program_interest);
    }
  }, [partnerParams]);

  const handleStart = async () => {
    setLoading(true);
    try {
      if (!sessionId) {
        const res = await axios.post(`${API}/session/create`, {
          partner: partnerParams.partner,
          educrib_session_id: partnerParams.educrib_session_id,
          educrib_user_id: partnerParams.educrib_user_id,
          prefill_path_type: partnerParams.prefill_path_type,
          prefill_location: partnerParams.prefill_location,
          prefill_program_interest: partnerParams.prefill_program_interest,
        });
        const sid = res.data.session_id;
        setSessionId(sid);
        sessionStorage.setItem("synaptick_session_id", sid);
        sessionStorage.setItem("synaptick_partner", partnerParams.partner);
        if (res.data.prefill?.path_type) sessionStorage.setItem("prefill_path_type", res.data.prefill.path_type);
        if (res.data.prefill?.location) sessionStorage.setItem("prefill_location", res.data.prefill.location);
        if (res.data.prefill?.program_interest) sessionStorage.setItem("prefill_program_interest", res.data.prefill.program_interest);
      }
      // Track session start
      const sid = sessionId || sessionStorage.getItem("synaptick_session_id");
      await axios.post(`${API}/analytics/event`, {
        event_type: "session_started", session_id: sid, data: { partner: partnerParams.partner }
      }).catch(() => {});

      navigate("/sprint");
    } catch (e) {
      console.error(e);
      // Even if create fails, navigate and create on-the-fly
      const fallbackSid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      sessionStorage.setItem("synaptick_session_id", fallbackSid);
      navigate("/sprint");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="hero-gradient relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight">
              Synaptick Clarity Layer
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Two minutes to clarify what to search for — and why.
            </p>
            <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
              We will ask a few quick questions to clarify what to look for on Educrib.
            </p>
            <Button
              data-testid="start-clarity-sprint-button"
              onClick={handleStart}
              disabled={loading}
              className="mt-8 rounded-[12px] shadow-sm hover:shadow-md px-6 h-12 text-base gap-2"
            >
              {loading ? "Setting up..." : "Start Clarity Sprint"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: <Clock className="w-5 h-5" />, title: "Under 2 minutes", desc: "Quick selections, minimal typing." },
            { icon: <Sparkles className="w-5 h-5" />, title: "Practical clarity", desc: "A brief with realistic guidance and search filters." },
            { icon: <Shield className="w-5 h-5" />, title: "Private by default", desc: "No login needed. Anonymised data only." }
          ].map((item, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5">
              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-primary mb-3">{item.icon}</div>
              <h3 className="font-display font-semibold text-sm text-foreground mb-1">{item.title}</h3>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <p className="text-xs text-center text-muted-foreground">
          This is guidance to help you decide what to explore — not official admissions or career advice.
        </p>
      </div>

      <footer className="max-w-3xl mx-auto px-4 sm:px-6 py-6 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Synaptick Clarity Layer</span>
          <a href="/readme" className="hover:text-foreground transition-colors">About</a>
        </div>
      </footer>
    </div>
  );
}
